/**
 * Utilitaires pour normaliser les messages d'erreur affichés aux utilisateurs
 *
 * Tous les messages d'erreur doivent être simples, compréhensibles
 * et ne jamais exposer de détails techniques (codes d'erreur, stack traces, etc.)
 */

interface ErrorLike {
  code?: string;
  message?: string;
  details?: any;
}

/**
 * Convertit une erreur technique en message simple pour l'utilisateur
 *
 * @param error - L'erreur à convertir (peut être Error, objet avec code/message, ou string)
 * @param context - Contexte de l'erreur pour personnaliser le message
 * @returns Message d'erreur simple et compréhensible
 */
export const getUserFriendlyError = (
  error: unknown,
  context?: {
    action?: string; // Action en cours (ex: "analyse", "upload", "connexion")
    resource?: string; // Ressource concernée (ex: "CV", "fichier")
  }
): string => {
  // Si c'est déjà une string simple, la retourner telle quelle
  if (typeof error === 'string') {
    // Vérifier si c'est déjà un message utilisateur (pas de codes techniques)
    if (!error.includes('/') && !error.includes('Error:') && !error.includes('at ')) {
      return error;
    }
  }

  // Extraire les informations de l'erreur
  let errorCode: string | undefined;
  let errorMessage: string | undefined;

  if (error instanceof Error) {
    errorCode = (error as any).code;
    errorMessage = error.message;
  } else if (typeof error === 'object' && error !== null) {
    const err = error as ErrorLike;
    errorCode = err.code;
    errorMessage = err.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  // Messages par défaut selon le contexte
  const defaultMessages = {
    analyse: 'Une erreur est survenue lors de l\'analyse. Veuillez réessayer.',
    upload: 'Une erreur est survenue lors du téléchargement. Veuillez réessayer.',
    connexion: 'Une erreur est survenue lors de la connexion. Veuillez réessayer.',
    sauvegarde: 'Une erreur est survenue lors de la sauvegarde. Veuillez réessayer.',
    envoi: 'Une erreur est survenue lors de l\'envoi. Veuillez réessayer.',
    chargement: 'Une erreur est survenue lors du chargement. Veuillez réessayer.',
    recherche: 'Une erreur est survenue lors de la recherche. Veuillez réessayer.',
    facturation: 'Une erreur est survenue. Veuillez réessayer ou contacter le support.',
    default: 'Une erreur est survenue. Veuillez réessayer.',
  };

  const defaultMessage = context?.action
    ? defaultMessages[context.action as keyof typeof defaultMessages] || defaultMessages.default
    : defaultMessages.default;

  // Gérer les codes d'erreur Firebase Auth
  if (errorCode?.startsWith('auth/')) {
    switch (errorCode) {
      case 'auth/popup-closed-by-user':
        return 'La fenêtre de connexion a été fermée. Veuillez réessayer.';
      case 'auth/popup-blocked':
        return 'La fenêtre de connexion a été bloquée. Veuillez autoriser les popups pour ce site.';
      case 'auth/cancelled-popup-request':
        return 'Une autre demande de connexion est en cours. Veuillez patienter.';
      case 'auth/account-exists-with-different-credential':
        return 'Un compte existe déjà avec une autre méthode de connexion.';
      case 'auth/network-request-failed':
        return 'Erreur de connexion réseau. Vérifiez votre connexion internet.';
      case 'auth/too-many-requests':
        return 'Trop de tentatives. Veuillez réessayer plus tard.';
      case 'auth/user-disabled':
        return 'Ce compte a été désactivé. Contactez le support.';
      case 'auth/user-not-found':
        return 'Aucun compte trouvé avec ces identifiants.';
      case 'auth/wrong-password':
        return 'Mot de passe incorrect.';
      case 'auth/email-already-in-use':
        return 'Cet email est déjà utilisé par un autre compte.';
      case 'auth/weak-password':
        return 'Le mot de passe est trop faible.';
      case 'auth/invalid-email':
        return 'L\'adresse email n\'est pas valide.';
      default:
        return 'Une erreur est survenue lors de la connexion. Veuillez réessayer.';
    }
  }

  // Gérer les codes d'erreur Firebase Functions
  if (errorCode?.startsWith('functions/')) {
    switch (errorCode) {
      case 'functions/unauthenticated':
        return 'Vous devez être connecté pour effectuer cette action.';
      case 'functions/permission-denied':
        return 'Vous n\'avez pas la permission d\'effectuer cette action.';
      case 'functions/not-found':
        return 'Service temporairement indisponible. Veuillez réessayer dans quelques instants.';
      case 'functions/deadline-exceeded':
        return 'L\'opération a pris trop de temps. Veuillez réessayer.';
      case 'functions/resource-exhausted': {
        // `resource-exhausted` recouvre DEUX cas très différents : une vraie
        // surcharge côté serveur, et un quota utilisateur atteint. Dans le second,
        // `checkRateLimit` renvoie un message actionnable et vendeur (« Limite
        // quotidienne atteinte (3 cv_adaptation/jour)… Passez à Premium pour un
        // accès illimité »). L'écraser par « Service temporairement surchargé »
        // transformait le moment exact où le paywall doit convertir en ce qui
        // ressemble à une panne — et faisait fuir l'utilisateur au lieu de le
        // faire payer. On préserve donc le message serveur quand il porte un quota.
        if (errorMessage && /limite|quota|premium|limit/i.test(errorMessage)) {
          return errorMessage;
        }
        return 'Service temporairement surchargé. Veuillez réessayer dans quelques instants.';
      }
      case 'functions/failed-precondition':
        return 'Les conditions requises ne sont pas remplies. Veuillez réessayer.';
      case 'functions/aborted':
        return 'L\'opération a été annulée. Veuillez réessayer.';
      case 'functions/out-of-range':
        return 'Les données fournies ne sont pas valides.';
      case 'functions/unimplemented':
        return 'Cette fonctionnalité n\'est pas encore disponible.';
      case 'functions/internal':
        return 'Une erreur interne est survenue. Veuillez réessayer.';
      case 'functions/unavailable':
        return 'Service temporairement indisponible. Veuillez réessayer dans quelques instants.';
      case 'functions/data-loss':
        return 'Une erreur est survenue avec vos données. Veuillez réessayer.';
      default:
        return defaultMessage;
    }
  }

  // Gérer les codes d'erreur Firestore
  if (errorCode?.startsWith('firestore/')) {
    switch (errorCode) {
      case 'firestore/permission-denied':
        return 'Vous n\'avez pas la permission d\'accéder à ces données.';
      case 'firestore/unavailable':
        return 'Service temporairement indisponible. Veuillez réessayer.';
      case 'firestore/deadline-exceeded':
        return 'L\'opération a pris trop de temps. Veuillez réessayer.';
      default:
        return defaultMessage;
    }
  }

  // Gérer les codes d'erreur HTTP
  if (errorCode?.startsWith('http/') || typeof errorCode === 'string') {
    const httpMatch = errorCode?.match(/^http\/(\d+)$/);
    if (httpMatch) {
      const statusCode = parseInt(httpMatch[1], 10);
      switch (statusCode) {
        case 400:
          return 'Les données fournies ne sont pas valides.';
        case 401:
          return 'Vous devez être connecté pour effectuer cette action.';
        case 403:
          return 'Vous n\'avez pas la permission d\'effectuer cette action.';
        case 404:
          return 'La ressource demandée n\'a pas été trouvée.';
        case 408:
          return 'L\'opération a pris trop de temps. Veuillez réessayer.';
        case 429:
          return 'Trop de requêtes. Veuillez réessayer dans quelques instants.';
        case 500:
        case 502:
        case 503:
        case 504:
          return 'Service temporairement indisponible. Veuillez réessayer dans quelques instants.';
        default:
          return defaultMessage;
      }
    }
  }

  // Si le message d'erreur contient des détails techniques, le remplacer
  if (errorMessage) {
    // Détecter les messages techniques à remplacer
    const technicalPatterns = [
      /Error:/i,
      /at\s+.*\.(js|ts|tsx):\d+:\d+/,
      /Stack\s+trace/i,
      /TypeError:/i,
      /ReferenceError:/i,
      /SyntaxError:/i,
      /Network\s+error/i,
      /CORS/i,
      /ECONNREFUSED/i,
      /ETIMEDOUT/i,
      /ENOTFOUND/i,
      /Failed\s+to\s+fetch/i,
      /fetch\s+failed/i,
    ];

    const isTechnical = technicalPatterns.some(pattern => pattern.test(errorMessage!));

    if (isTechnical) {
      return defaultMessage;
    }

    // Si le message contient des chemins de fichiers ou des URLs techniques
    if (
      errorMessage.includes('http://') ||
      errorMessage.includes('https://') ||
      errorMessage.includes('/functions/') ||
      errorMessage.includes('/webhook/') ||
      errorMessage.includes('localhost') ||
      errorMessage.includes('127.0.0.1')
    ) {
      return defaultMessage;
    }

    // Si le message contient des codes d'erreur techniques
    if (errorMessage.match(/[a-z]+\/[a-z-]+/)) {
      return defaultMessage;
    }

    // Si le message est trop long (probablement une stack trace)
    if (errorMessage.length > 200) {
      return defaultMessage;
    }

    // Sinon, utiliser le message tel quel s'il semble être un message utilisateur
    return errorMessage;
  }

  // Par défaut, retourner un message générique
  return defaultMessage;
};

/**
 * Vérifie si une erreur est une erreur réseau
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection') ||
      message.includes('timeout') ||
      message.includes('econnrefused') ||
      message.includes('etimedout') ||
      message.includes('enotfound')
    );
  }
  return false;
};

/**
 * Vérifie si une erreur est une erreur de permission
 */
export const isPermissionError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const code = (error as any).code;
    return (
      code === 'functions/permission-denied' ||
      code === 'firestore/permission-denied' ||
      code === 'auth/permission-denied'
    );
  }
  return false;
};

/**
 * Vérifie si une erreur est une erreur d'authentification
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const code = (error as any).code;
    return (
      code === 'functions/unauthenticated' ||
      code === 'auth/unauthenticated' ||
      code?.startsWith('auth/')
    );
  }
  return false;
};
